import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';

// ── Icons ──────────────────────────────────────────────────────────────────
var IconSparkle = function() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.3L19 10l-5.1 1.7L12 17l-1.9-5.3L5 10l5.1-1.7z"/><path d="M18.5 16.5l.7 1.9 1.8.6-1.8.6-.7 1.9-.7-1.9-1.8-.6 1.8-.6z"/></svg>; };
var IconSend    = function() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>; };
var IconTrash   = function() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>; };

// ── Language direction ─────────────────────────────────────────────────────
// Arabic block + Arabic supplement/extended ranges.
var ARABIC_RE = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;
function isArabic(text) {
  return ARABIC_RE.test(text || '');
}

// ── Light markdown rendering ───────────────────────────────────────────────
// Handles **bold**, `code`, bullet lists, numbered lists and line breaks.
// Deliberately small — the model is told to answer concisely, not in rich HTML.
function renderInline(text, keyPrefix) {
  var parts = [];
  // Split on **bold** and `code`, keeping the delimiters.
  var tokens = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  for (var i = 0; i < tokens.length; i++) {
    var t = tokens[i];
    if (!t) continue;
    if (t.startsWith('**') && t.endsWith('**') && t.length > 4) {
      parts.push(<strong key={keyPrefix + '-b' + i} style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{t.slice(2, -2)}</strong>);
    } else if (t.startsWith('`') && t.endsWith('`') && t.length > 2) {
      parts.push(
        <code key={keyPrefix + '-c' + i} style={{
          fontFamily: 'var(--font-mono)', fontSize: '0.92em',
          background: 'var(--bg-input)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xs)', padding: '1px 5px',
        }}>{t.slice(1, -1)}</code>
      );
    } else {
      parts.push(<React.Fragment key={keyPrefix + '-t' + i}>{t}</React.Fragment>);
    }
  }
  return parts;
}

// A markdown table row: | a | b | c |
function isTableRow(line) {
  return /^\|.*\|$/.test(line.trim());
}
// The |---|:--:|---| separator directly under the header row
function isTableSeparator(line) {
  return /^\|[\s:|-]+\|$/.test(line.trim()) && line.indexOf('-') !== -1;
}
function splitTableRow(line) {
  var t = line.trim();
  return t.slice(1, -1).split('|').map(function(c) { return c.trim(); });
}

function MarkdownLite({ text }) {
  var lines = (text || '').split('\n');
  var blocks = [];
  var listBuffer = [];
  var listType = null;

  function flushList() {
    if (listBuffer.length === 0) return;
    var items = listBuffer.map(function(item, idx) {
      return <li key={'li' + blocks.length + '-' + idx} style={{ marginBottom: 3 }}>{renderInline(item, 'li' + blocks.length + '-' + idx)}</li>;
    });
    var style = { margin: '6px 0', paddingInlineStart: 20, lineHeight: 1.65 };
    blocks.push(listType === 'ol'
      ? <ol key={'blk' + blocks.length} style={style}>{items}</ol>
      : <ul key={'blk' + blocks.length} style={style}>{items}</ul>);
    listBuffer = [];
    listType = null;
  }

  for (var i = 0; i < lines.length; i++) {
    var line = lines[i];
    var trimmed = line.trim();

    // Table: header row + separator + body rows
    if (isTableRow(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      flushList();
      var headers = splitTableRow(line);
      var rows = [];
      i += 2; // skip header + separator
      while (i < lines.length && isTableRow(lines[i])) {
        rows.push(splitTableRow(lines[i]));
        i++;
      }
      i--; // the outer loop increments
      var tKey = 'blk' + blocks.length;
      blocks.push(
        <div key={tKey} style={{ overflowX: 'auto', margin: '8px 0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr>
                {headers.map(function(h, hi) {
                  return (
                    <th key={hi} style={{
                      textAlign: 'start', padding: '6px 10px', fontWeight: 600,
                      color: 'var(--text-muted)', fontSize: 11,
                      borderBottom: '1px solid var(--border-strong, var(--border))',
                      whiteSpace: 'nowrap',
                    }}>{renderInline(h, tKey + '-h' + hi)}</th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map(function(r, ri) {
                return (
                  <tr key={ri} style={{ borderBottom: '1px solid var(--border)' }}>
                    {r.map(function(c, ci) {
                      return (
                        <td key={ci} style={{
                          padding: '6px 10px', color: 'var(--text-secondary)',
                          verticalAlign: 'top',
                        }}>{renderInline(c, tKey + '-r' + ri + 'c' + ci)}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      flushList();
      blocks.push(
        <div key={'blk' + blocks.length} style={{
          borderTop: '1px solid var(--border)', margin: '12px 0',
        }} />
      );
      continue;
    }

    // Blockquote / callout
    var quote = trimmed.match(/^>\s?(.*)$/);
    if (quote) {
      flushList();
      blocks.push(
        <div key={'blk' + blocks.length} style={{
          borderInlineStart: '3px solid var(--primary-border)',
          background: 'var(--primary-subtle)',
          borderRadius: 'var(--radius-xs)',
          padding: '8px 12px', margin: '8px 0', lineHeight: 1.6,
        }}>{renderInline(quote[1], 'q' + i)}</div>
      );
      continue;
    }

    var bullet = trimmed.match(/^[-*•]\s+(.*)$/);
    var numbered = trimmed.match(/^\d+[.)]\s+(.*)$/);

    if (bullet) {
      if (listType === 'ol') flushList();
      listType = 'ul';
      listBuffer.push(bullet[1]);
      continue;
    }
    if (numbered) {
      if (listType === 'ul') flushList();
      listType = 'ol';
      listBuffer.push(numbered[1]);
      continue;
    }

    flushList();

    if (trimmed === '') continue; // collapse blank lines between blocks

    // Markdown headings become emphasized lines rather than huge <h> tags.
    var heading = trimmed.match(/^#{1,4}\s+(.*)$/);
    if (heading) {
      blocks.push(
        <div key={'blk' + blocks.length} style={{ fontWeight: 700, color: 'var(--text-primary)', margin: '10px 0 4px' }}>
          {renderInline(heading[1], 'h' + i)}
        </div>
      );
      continue;
    }

    blocks.push(
      <p key={'blk' + blocks.length} style={{ margin: '0 0 8px', lineHeight: 1.65 }}>
        {renderInline(trimmed, 'p' + i)}
      </p>
    );
  }
  flushList();

  return <div>{blocks}</div>;
}

// ── Suggested questions (empty state) ──────────────────────────────────────
var SUGGESTIONS = [
  'أعطني ملخص عن حالة النظام الآن',
  'كم نطاق فيه مشاكل في DNS؟',
  'وش أكثر 5 خدمات تعطلت آخر 30 يوم؟',
  'Which domains are missing DMARC?',
];

// ── Thinking indicator ─────────────────────────────────────────────────────
function ThinkingBubble() {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 14 }}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg, 14px)', borderTopLeftRadius: 4,
        padding: '12px 16px', boxShadow: 'var(--shadow-sm)',
        display: 'flex', alignItems: 'center', gap: 9,
      }}>
        <span style={{ display: 'inline-flex', gap: 4 }}>
          {[0, 1, 2].map(function(i) {
            return <span key={i} className="ai-dot" style={{ animationDelay: (i * 0.18) + 's' }} />;
          })}
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Thinking…</span>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
function AIAssistant({ token, user }) {
  var [messages, setMessages]   = useState([]);
  var [input, setInput]         = useState('');
  var [sending, setSending]     = useState(false);
  var [loading, setLoading]     = useState(true);
  var [error, setError]         = useState('');
  // null = unknown (status check failed) — let the user try; false = not set up.
  var [configured, setConfigured] = useState(null);
  var scrollRef = useRef(null);
  var textareaRef = useRef(null);

  // Load previous history on mount
  useEffect(function() {
    api.get('/ai/status')
      .then(function(res) { setConfigured(!!res.data.configured); })
      .catch(function() { setConfigured(null); });

    api.get('/ai/chat/history')
      .then(function(res) {
        setMessages(res.data.map(function(m) {
          return { role: m.role, content: m.content };
        }));
      })
      .catch(function(err) {
        console.error('Failed to load AI history:', err);
      })
      .finally(function() {
        setLoading(false);
      });
  }, []);

  // Auto-scroll to the newest message
  useEffect(function() {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  var send = useCallback(async function(text) {
    var msg = (text || '').trim();
    if (!msg || sending) return;

    setError('');
    setInput('');
    // The textarea auto-grows via an inline height on input; clearing the value
    // alone would leave a tall empty box behind after a multi-line question.
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setMessages(function(prev) { return prev.concat([{ role: 'user', content: msg }]); });
    setSending(true);

    try {
      var res = await api.post('/ai/chat', { message: msg });
      setMessages(function(prev) { return prev.concat([{ role: 'assistant', content: res.data.answer }]); });
    } catch (err) {
      if (err.response?.status === 503) {
        setConfigured(false);
        // Nothing was answered or saved — drop the optimistic bubble.
        setMessages(function(prev) { return prev.slice(0, -1); });
        return;
      }
      var apiError = err.response?.data?.error;
      setError(apiError || 'Could not reach the AI service. Please try again.');
    } finally {
      setSending(false);
    }
  }, [sending]);

  var handleKeyDown = function(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  var handleClear = async function() {
    if (!window.confirm('Clear this conversation? This cannot be undone.')) return;
    try {
      await api.delete('/ai/chat/history');
      setMessages([]);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear the conversation.');
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner" /><span>Loading conversation…</span></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px)' }}>
      {/* Dot animation for the Thinking indicator — scoped to this page */}
      <style>{
        '.ai-dot{width:6px;height:6px;border-radius:50%;background:var(--primary);' +
        'display:inline-block;animation:aiDotBounce 1.1s ease-in-out infinite}' +
        '@keyframes aiDotBounce{0%,80%,100%{opacity:.28;transform:translateY(0)}' +
        '40%{opacity:1;transform:translateY(-3px)}}'
      }</style>

      <div className="page-header" style={{ flexShrink: 0 }}>
        <div className="page-header-top">
          <div>
            <h1>AI Assistant</h1>
            <p>Ask about your uptime, DNS and SSL data in Arabic or English — answers come from the live database</p>
          </div>
          {messages.length > 0 && (
            <div className="page-actions">
              <button className="btn btn-secondary" onClick={handleClear}>
                <IconTrash /> Clear conversation
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Message list */}
      <div
        ref={scrollRef}
        style={{
          flex: 1, overflowY: 'auto', padding: '4px 2px 16px',
          minHeight: 0,
        }}
      >
        {messages.length === 0 && !sending ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', height: '100%', textAlign: 'center',
            padding: '24px 16px',
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: 'var(--radius-md)',
              background: 'var(--primary-subtle)', border: '1px solid var(--primary-border)',
              color: 'var(--primary)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', marginBottom: 16,
            }}>
              <span style={{ display: 'inline-flex', width: 26, height: 26 }}><IconSparkle /></span>
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>
              Ask about your infrastructure
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 460, marginBottom: 22, lineHeight: 1.6 }}>
              The assistant reads your real monitoring data — uptime history, DNS records,
              SSL certificates and incidents — and answers with actual numbers.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', maxWidth: 620 }}>
              {SUGGESTIONS.map(function(q) {
                return (
                  <button
                    key={q}
                    onClick={function() { send(q); }}
                    disabled={configured === false}
                    dir={isArabic(q) ? 'rtl' : 'ltr'}
                    style={{
                      padding: '8px 14px', fontSize: 12.5, fontWeight: 500,
                      fontFamily: 'var(--font)', cursor: 'pointer',
                      background: 'var(--bg-card)', color: 'var(--text-secondary)',
                      border: '1px solid var(--border)', borderRadius: 'var(--radius-full)',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={function(e) {
                      e.currentTarget.style.borderColor = 'var(--primary)';
                      e.currentTarget.style.color = 'var(--primary)';
                    }}
                    onMouseLeave={function(e) {
                      e.currentTarget.style.borderColor = 'var(--border)';
                      e.currentTarget.style.color = 'var(--text-secondary)';
                    }}
                  >
                    {q}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: 820, margin: '0 auto' }}>
            {messages.map(function(m, idx) {
              var mine = m.role === 'user';
              var rtl = isArabic(m.content);
              return (
                <div key={idx} style={{
                  display: 'flex',
                  justifyContent: mine ? 'flex-end' : 'flex-start',
                  marginBottom: 14,
                }}>
                  <div
                    dir={rtl ? 'rtl' : 'ltr'}
                    style={{
                      maxWidth: '78%',
                      padding: '11px 15px',
                      fontSize: 13.5,
                      borderRadius: 'var(--radius-lg, 14px)',
                      ...(mine
                        ? {
                            background: 'var(--primary)',
                            color: 'var(--on-primary)',
                            borderTopRightRadius: 4,
                          }
                        : {
                            background: 'var(--bg-card)',
                            color: 'var(--text-secondary)',
                            border: '1px solid var(--border)',
                            borderTopLeftRadius: 4,
                            boxShadow: 'var(--shadow-sm)',
                          }),
                      textAlign: rtl ? 'right' : 'left',
                      wordBreak: 'break-word',
                    }}
                  >
                    {mine
                      ? <div style={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{m.content}</div>
                      : <MarkdownLite text={m.content} />}
                  </div>
                </div>
              );
            })}
            {sending && <ThinkingBubble />}
          </div>
        )}
      </div>

      {/* Input area */}
      <div style={{
        flexShrink: 0, borderTop: '1px solid var(--border)',
        paddingTop: 14, background: 'var(--bg-page, transparent)',
      }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }}>
          {configured === false && (
            <div className="alert alert-warning" style={{ marginBottom: 10 }}>
              The AI assistant isn't set up yet. An administrator needs to add an
              Anthropic API key on the server to turn it on. Everything else in
              Shaheen keeps working as normal.
            </div>
          )}
          {error && <div className="alert alert-error" style={{ marginBottom: 10 }}>{error}</div>}

          <div style={{
            display: 'flex', gap: 8, alignItems: 'flex-end',
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)', padding: 8,
          }}>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={function(e) { setInput(e.target.value); }}
              onKeyDown={handleKeyDown}
              dir={isArabic(input) ? 'rtl' : 'ltr'}
              placeholder="Ask anything about your monitoring data…"
              rows={1}
              disabled={sending || configured === false}
              style={{
                flex: 1, resize: 'none', border: 'none', outline: 'none',
                background: 'transparent', color: 'var(--text-primary)',
                fontFamily: 'var(--font)', fontSize: 13.5, lineHeight: 1.6,
                padding: '6px 6px', maxHeight: 160, minHeight: 24,
                textAlign: isArabic(input) ? 'right' : 'left',
              }}
              onInput={function(e) {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
              }}
            />
            <button
              className="btn btn-primary"
              onClick={function() { send(input); }}
              disabled={sending || configured === false || !input.trim()}
              style={{ flexShrink: 0 }}
            >
              <IconSend /> {sending ? 'Sending…' : 'Send'}
            </button>
          </div>

          <div style={{
            fontSize: 10.5, color: 'var(--text-muted)', marginTop: 7,
            display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
          }}>
            <span>Enter to send · Shift+Enter for a new line</span>
            <span>AI responses use Anthropic API credits.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIAssistant;
